/**
 * RBAC v2 — nouvelle taxonomie de permissions (derrière le flag Flipt `permission-v2`).
 *
 * Modélisation explicite « à la CASL » : un unique objet général `rbacV2Abilities` liste chaque
 * ressource (clé concaténée `module.sous-onglet.fonctionnalité`) → tableau EXACT de ses actions.
 * On connaît ainsi précisément, au type-level, quelles actions existent sur chaque ressource.
 *
 * - Les actions partagées (`view/add/edit/delete/export/import`) existent sur plusieurs ressources.
 * - Chaque action spéciale est une action nommée à part entière, portée par UNE seule ressource.
 *
 * La notion de `scope` v1 n'existe plus en v2. La clé de ressource est stockée telle quelle dans
 * `role_permissions.resource`, et l'action dans `role_permissions.permission_type` (scope = NULL).
 *
 * Le module de permissions v1 (`./rbac.ts`) reste inchangé et coexiste avec cette v2.
 */

/** `feature_flags`/Flipt : gate d'activation de l'UI et du modèle v2. */
export const PERMISSION_V2_FLAG = 'permission-v2' as const

/** Actions partagées par plusieurs ressources (colonnes fixes de la matrice). */
export const RBAC_V2_SHARED_ACTIONS = ['read', 'add', 'edit', 'delete', 'export', 'import'] as const

/**
 * Actions spéciales : chacune n'est portée que par la ressource à laquelle elle est associée.
 * (Colonne « Action spécifique » de la matrice.)
 */
export const RBAC_V2_SPECIAL_ACTIONS = [
  'launch-reappro-simulation',
  'send-ban-email',
  'assign-bo-role',
  'validate-inventory',
  'sync-schedule',
  'restart-kiosk',
  'enter-support-mode',
  'launch-sales-calculation',
  'validate-clocking',
  'resend-unread-sms',
  'archive-real-estate-project',
  'start-intervention',
] as const

export const RBAC_V2_ACTIONS = [...RBAC_V2_SHARED_ACTIONS, ...RBAC_V2_SPECIAL_ACTIONS] as const

export type RbacV2SharedAction = (typeof RBAC_V2_SHARED_ACTIONS)[number]
export type RbacV2SpecialAction = (typeof RBAC_V2_SPECIAL_ACTIONS)[number]
export type RbacV2Action = RbacV2SharedAction | RbacV2SpecialAction

const FULL = ['read', 'add', 'edit', 'delete', 'export', 'import'] as const

/**
 * OBJET GÉNÉRAL — source de vérité de la taxonomie v2.
 * Clé = `module.sous-onglet.fonctionnalité` (= valeur stockée dans `role_permissions.resource`).
 * Valeur = tableau exact des actions disponibles pour cette ressource.
 * Un tableau vide = fonctionnalité présente dans la carte mais sans action configurable (module désactivé).
 */
export const rbacV2Abilities = {
  // 1. Tableau de bord — module migré : l'écran est en lecture seule, seuls ses exports XLSX
  // (ventes, livraisons, audit, audit hebdomadaire) ajoutent une action.
  'dashboard.dashboard.global-indicators': ['read', 'export'],

  // 2. Aujourd'hui — module migré : écran de consultation seul, aucune mutation ni export.
  'today.today.daily-tracking': ['read'],

  // 3. Paniers — module migré : liste, détail et fiche client sont en lecture seule ; seul
  // l'export des tickets ajoute une action. Aucune affordance de création/édition/suppression.
  'carts.carts.cart': ['read', 'export'],
  // La vérification visuelle est un second écran du module : on consulte les contrôles produits
  // par la reconnaissance d'image et leurs statistiques, et `edit` couvre leur seul arbitrage
  // manuel (confirmer ou infirmer un contrôle). Rien ne s'y crée, ne s'y supprime ni ne s'exporte.
  'carts.vision-checks.check': ['read', 'edit'],

  // 4. Catégories — module migré : chaque action listée correspond à une affordance réelle
  'categories.categories.standard': ['read', 'add', 'edit', 'delete'],
  'categories.internal-categories.internal': ['read', 'add', 'edit', 'delete', 'export'],
  'categories.reappro.category': ['read', 'add', 'edit', 'delete'],
  'categories.reappro.config': ['read', 'edit', 'delete', 'export', 'import'],
  'categories.reappro.simulation': ['export', 'import', 'launch-reappro-simulation'],
  'categories.brands.brand': ['read', 'add', 'edit', 'delete'],

  // 5. Produits
  'products.products.product': FULL,

  // 6. Stock Plateforme
  'platform-stock.platform-stock.stock': FULL,

  // 7. Utilisateurs — module migré : chaque action listée correspond à une affordance réelle.
  // L'onglet « Étudiants » n'existe plus dans l'app : sa ressource a été retirée.
  'users.app-users.user': ['read', 'edit', 'delete'],
  'users.app-users.ban': ['edit', 'send-ban-email'],
  'users.app-users.solvency': ['read'],
  'users.payment-defaults.default': ['read'],
  // `assign-bo-role` : attribuer des droits au compte. Créer ou modifier une fiche reste possible
  // sans elle — elle ne conditionne que le choix des droits.
  'users.bo-users.user': ['read', 'add', 'edit', 'delete', 'assign-bo-role'],
  // Distinct de la fiche : une clé ouvre un accès programmatique à l'API avec les droits du compte.
  // `edit` = élargir les abilities machine d'une clé existante (jamais les restreindre).
  'users.bo-users.api-key': ['read', 'add', 'edit', 'delete'],

  // 8. Campagne promo — module migré : pas d'export/import de campagnes dans l'app. Les codes à
  // usage unique n'ont pas d'écran propre : les générer EST leur seule création, d'où `add` sans
  // action spéciale dédiée.
  'promo.temporary-campaigns.campaign': ['read', 'add', 'edit', 'delete'],
  'promo.temporary-campaigns.single-use-codes': ['add', 'export'],

  // 9. Partenariats CE
  'works-council.works-council.partnership': ['read', 'add', 'edit', 'delete'],

  // 10. Notifications — module migré : la liste et le détail sont en lecture seule, et la seule
  // mutation est l'envoi (le formulaire « Créer une notification »). Une notification envoyée n'est
  // ni modifiable ni supprimable, et l'écran n'offre ni export ni import.
  'notifications.notifications.notification': ['read', 'add'],
  // Dupliquer, c'est renvoyer une notification pré-remplie depuis une existante : sa seule action
  // est donc l'envoi, distinct de la création à partir de zéro.
  'notifications.notifications.duplication': ['add'],

  // 11. Boutiques — module migré : chaque action listée correspond à une affordance réelle.
  // Aucune boutique ne s'importe : `import` a été retiré de toutes les ressources du module.
  'stores.store-list.store': ['read', 'add', 'edit', 'delete', 'export'],
  // Écran de consultation : le BO ne crée, ne modifie ni ne supprime le stock d'une boutique (il
  // vient des livraisons et des inventaires). Seul l'export CSV s'y ajoute.
  'stores.store-stocks.stock': ['read', 'export'],
  // Un inventaire est ouvert par la cabine, jamais depuis le BO : pas de `add`. `delete` = annuler
  // l'inventaire en cours. L'export de son détail est porté par `all-inventories.history`.
  'stores.current-inventory.inventory': ['read', 'edit', 'delete'],
  // Seule l'action spéciale : le reste du cycle de vie est porté par `current-inventory.inventory`.
  'stores.current-inventory.validation': ['validate-inventory'],
  'stores.all-inventories.history': ['read', 'edit', 'export'],
  // « Enregistrer & synchroniser » exige `edit` en plus de l'action spéciale.
  'stores.schedules.schedule': ['read', 'edit', 'sync-schedule'],
  // Les kiosks sont déclarés côté CPI : le BO ne fait que les lister et les redémarrer. `edit` sert
  // la branche back-office des routes de porte partagées avec la cabine (`/kiosk/:id/open-client`).
  'stores.kiosks.kiosk': ['read', 'edit', 'restart-kiosk'],
  // Les exports (CSV/PDF des cartes de la période) sont calculés côté client, mais restent un droit
  // à part entière : lire les statistiques ne donne pas le droit de les sortir de l'outil.
  'stores.statistics.statistics': ['read', 'export'],

  // 12. Comptabilité — module migré : un écran unique, en lecture seule, dont chaque bloc d'export
  // porte sa propre ressource. Aucune affordance de création/édition/suppression/import.
  'accounting.accounting.figures': ['read', 'export'],
  'accounting.accounting.balance': ['export'],
  'accounting.accounting.payments': ['export'],
  'accounting.accounting.works-council-history': ['export'],
  'accounting.accounting.warnings': ['read'],

  // 13. Qualité — module migré : deux onglets de consultation, sans création, suppression, export
  // ni import. Seul le détail d'un lot mute (blocage/déblocage et commentaire), d'où `edit` sur la
  // seule traçabilité par lot.
  'quality.search-by-lot.lot-traceability': ['read', 'edit'],
  'quality.search-by-article.article-traceability': ['read'],

  // 14. Contrôle panier — module migré : un écran unique, entièrement suspendu à la prise de main
  // sur la caisse. Rien n'y est lisible ni mutable sans mode support (les routes `kiosks/*` de
  // manipulation d'articles le vérifient elles-mêmes), donc le module tient en une seule action.
  // La ressource `live-control` a disparu : aucune de ses actions ne portait un droit distinct.
  'cart-control.cart-control.support-mode': ['enter-support-mode'],

  // 15. Ventes journalières — module migré : un écran unique (moyennes vendues + stocks minimum
  // par jour), sans création ni suppression de ligne. Le panneau « Calcul automatique » est une
  // ressource à part : on y lit et sauvegarde les paramètres de calcul d'une catégorie, et on lance
  // le calcul — mais rien à créer, supprimer, exporter ni importer.
  'daily-sales.daily-sales.daily-sales': ['read', 'edit', 'export', 'import'],
  'daily-sales.daily-sales.auto-calculation': ['read', 'edit', 'launch-sales-calculation'],

  // 16. Vendeurs — module migré : deux onglets (vendeurs, établissements) avec le même CRUD
  // complet. L'écran n'offre ni export ni import.
  'sellers.sellers.seller': ['read', 'add', 'edit', 'delete'],
  'sellers.establishments.establishment': ['read', 'add', 'edit', 'delete'],

  // 17. Caméras — module migré : ni export ni import dans l'app. Le flux se lit (écran « Caméras »)
  // et se compose en vues personnalisées, d'où add/edit/delete sur la même ressource.
  // `start-intervention` : ouvrir un appel audio/vidéo sur la caisse depuis un flux. Lire le flux
  // ne suffit pas — l'intervention parle au client présent en boutique.
  'cameras.cameras.stream': ['read', 'add', 'edit', 'delete', 'start-intervention'],
  'cameras.cameras.config': ['read', 'add', 'edit', 'delete'],

  // 18. Capteurs (pas d'export/import)
  'sensors.sensors.sensor': ['read', 'add', 'edit', 'delete'],
  'sensors.fridges.fridge': ['read', 'add', 'edit', 'delete'],

  // 19. RH & Planning — module migré : quatre sous-onglets, un par ressource. Rien ne s'importe
  // hors des fiches collaborateur (`import` retiré du planning, du pointage et des absences).
  'hr.planning.weekly-planning': ['read', 'add', 'edit', 'delete', 'export'],
  'hr.staff.collaborator': FULL,
  // Un pointage se crée (tablette en boutique), se corrige via ses heures validées et s'exporte,
  // mais ne se supprime jamais : `delete` retiré.
  'hr.clocking.clocking': ['read', 'add', 'edit', 'export', 'validate-clocking'],
  'hr.absences.absence': ['read', 'add', 'edit', 'delete', 'export'],

  // 20. Suivi des SMS — module migré : un écran unique (tableau + compteurs + export CSV), dont la
  // relance des non-lus est une action de plus sur ces mêmes SMS, pas une ressource à part.
  'sms.sms.sent': ['read', 'export', 'resend-unread-sms'],

  // 21. Contrôle Interventions (Visio) — module migré : trois listes de consultation et un écran
  // d'administration. Les échanges naissent des appels visio et sont notés par le cron d'évaluation
  // IA : rien ne se crée, ne se supprime, ne s'exporte ni ne s'importe depuis le BO.
  'visio.yesterday-interactions.interaction': ['read'],
  'visio.exchanges-to-evaluate.exchange': ['read'],
  // La modale d'évaluation s'ouvre depuis n'importe laquelle des trois listes : la lire relève du
  // droit de lecture de la liste d'où elle est ouverte, d'où `edit` seul ici — noter l'échange,
  // valider la note IA, changer son statut de résolution.
  'visio.exchanges-to-evaluate.evaluation': ['edit'],
  'visio.overview.overview': ['read'],
  // Critères d'évaluation de l'IA et seuils de notation : une configuration unique, qui existe
  // d'office (ni création ni suppression).
  'visio.administration.administration': ['read', 'edit'],

  // 22. Foncier & travaux — module migré. La carte est le seul onglet à porter un import
  // (pivot des ouvertures) ; l'archivage n'est pas un écran, seulement une action sur un projet ;
  // aucun des quatre derniers onglets n'expose d'import ni d'export.
  'real-estate.map.map': FULL,
  'real-estate.kanban.project': ['read', 'add', 'edit', 'delete', 'export'],
  'real-estate.kanban.archive': ['edit', 'archive-real-estate-project'],
  'real-estate.tasks.task': ['read', 'add', 'edit', 'delete'],
  'real-estate.calendar.calendar': ['read', 'add', 'edit', 'delete'],
  'real-estate.contacts.contact': ['read', 'add', 'edit', 'delete'],
  'real-estate.areas-of-interest.area': ['read', 'add', 'edit', 'delete'],

  // 23. Réseau & Boutiques — module migré
  'network.network.network-dashboard': ['read', 'export'],

  // 24. Listes de diffusion — module migré : les listes elles-mêmes sont fixées en base (aucune
  // création ni suppression de liste), l'écran ne gère que leurs destinataires et n'offre ni
  // export ni import.
  'mailing-lists.mailing-lists.mailing-list': ['read', 'add', 'edit', 'delete'],

  // 25. Administration › Rôles — module migré : liste des rôles, détail, et formulaire partagé
  // création/édition portant la matrice de permissions. Un rôle ne se supprime pas depuis le BO, ne
  // s'exporte pas et ne s'importe pas.
  'roles-admin.roles.role': ['read', 'add', 'edit'],
  // Dialogue « Assigner des utilisateurs » : remplacement complet de la liste d'admins d'un rôle,
  // d'où la seule action `edit`. Le sens inverse — attribuer un rôle depuis la fiche d'un
  // utilisateur BO — est porté par `assign-bo-role` sur `users.bo-users.user`.
  'roles-admin.roles.admin-assignment': ['edit'],

  // 26. Feature Flags (module désactivé)
  'feature-flags.feature-flags.feature-flag': [],

  // 27. Prévisions — module migré : un écran unique en lecture seule (onglets « Prévision » et
  // « Backtest »), derrière le flag `CAForecasting`. Les paramètres de calcul et la sélection de
  // boutiques ne se persistent pas — ils vivent dans l'URL et le temps d'une requête —, et l'écran
  // n'offre ni export ni import : `read` est sa seule action.
  'previsions.previsions.forecast': ['read'],
  // 28. Pré-autorisations — module migré : la liste des sessions d'ouverture et la relecture de
  // leurs enregistrements vidéo se lisent ; `edit` couvre la seule mutation de l'écran, conserver
  // le montant pré-autorisé. Une session ne se crée, ne se supprime, ne s'exporte ni ne s'importe
  // depuis le BO.
  'pre-authorizations.pre-authorizations.session': ['read', 'edit'],
  // 29. Agences d'intérim — module migré : un écran unique, référentiel des agences alimentant le
  // formulaire de contrat d'une fiche collaborateur. L'archivage est le seul retrait possible
  // (soft-delete), le désarchivage relève de `edit`. Ni export ni import.
  'interim-agencies.interim-agencies.agency': ['read', 'add', 'edit', 'delete'],
  // 30. Suivi projet client — page `/suivi-projet`, ouverte aux comptes client : ils lisent le
  // board (kanban, synthèses, fiche) et n'y font que deux gestes, tous deux des ajouts — la
  // recette d'un ticket livré en staging (valider / signaler un souci) et le commentaire sur une
  // fiche. Rien ne s'y crée, ne s'y modifie, ne s'y supprime, ne s'exporte ni ne s'importe.
  'project-tracking.board.ticket': ['read'],
  'project-tracking.board.recette': ['add'],
  'project-tracking.board.comment': ['add'],
} as const satisfies Record<string, readonly RbacV2Action[]>

export type RbacV2Resource = keyof typeof rbacV2Abilities

/**
 * Union de tuples `[action, ressource]` dérivée de `rbacV2Abilities`.
 * Prête pour typer un `MongoAbility<RbacV2AbilityTuple>` si l'enforcement v2 est câblé plus tard.
 */
export type RbacV2AbilityTuple = {
  [K in RbacV2Resource]: [(typeof rbacV2Abilities)[K][number], K]
}[RbacV2Resource]

export const rbacV2Resources = Object.keys(rbacV2Abilities) as RbacV2Resource[]

// --- Périmètres (scopes) ----------------------------------------------------------------------

/**
 * Périmètre d'une permission. `global` est le défaut implicite de toutes ; les autres ne sont
 * proposés que sur les couples (ressource, action) déclarés dans `RBAC_V2_SCOPED_PERMISSIONS`.
 */
export const RBAC_V2_SCOPES = ['global', 'real-estate-restricted'] as const
export type RbacV2Scope = (typeof RBAC_V2_SCOPES)[number]

export const RBAC_V2_SCOPE_LABELS: Record<RbacV2Scope, string> = {
  global: 'Global',
  'real-estate-restricted': 'Prez projet',
}

const GLOBAL_ONLY = ['global'] as const satisfies readonly RbacV2Scope[]
const REAL_ESTATE_SCOPES = [
  'global',
  'real-estate-restricted',
] as const satisfies readonly RbacV2Scope[]

/**
 * Permissions à périmètre, reprise du périmètre v1 « Prez projet » du module Foncier & travaux :
 * un rôle ainsi restreint n'écrit que sur les projets au statut `before_project` — et, par
 * ricochet, sur les tâches, événements, documents et photos qui leur sont rattachés, dont les
 * contrôleurs passent par la même garde de périmètre.
 *
 * Seules les écritures sur le projet lui-même portent le périmètre : la lecture ne l'a jamais été,
 * et les onglets sans projet (Contacts, Zones d'intérêt) n'ont rien à restreindre.
 */
export const RBAC_V2_SCOPED_PERMISSIONS = {
  'real-estate.map.map': { actions: ['add', 'edit', 'delete'], scopes: REAL_ESTATE_SCOPES },
  'real-estate.kanban.project': { actions: ['add', 'edit', 'delete'], scopes: REAL_ESTATE_SCOPES },
  'real-estate.kanban.archive': {
    actions: ['edit', 'archive-real-estate-project'],
    scopes: REAL_ESTATE_SCOPES,
  },
} as const satisfies Partial<
  Record<RbacV2Resource, { actions: readonly RbacV2Action[]; scopes: readonly RbacV2Scope[] }>
>

/** Ressources dont les écritures portent le périmètre foncier — source unique côté enforcement. */
export const RBAC_V2_SCOPED_RESOURCES = Object.keys(
  RBAC_V2_SCOPED_PERMISSIONS,
) as (keyof typeof RBAC_V2_SCOPED_PERMISSIONS)[]

/** Périmètres proposés pour un couple donné ; `['global']` quand il n'est pas à périmètre. */
export const getRbacV2Scopes = (resource: string, action: string): readonly RbacV2Scope[] => {
  const entry = (
    RBAC_V2_SCOPED_PERMISSIONS as Record<
      string,
      { actions: readonly string[]; scopes: readonly RbacV2Scope[] } | undefined
    >
  )[resource]

  return entry?.actions.includes(action) ? entry.scopes : GLOBAL_ONLY
}

/** Un couple non déclaré à périmètre n'accepte que `global` (bord d'écriture). */
export const isRbacV2ScopeAllowed = (resource: string, action: string, scope: string): boolean =>
  (getRbacV2Scopes(resource, action) as readonly string[]).includes(scope)

/** Valide qu'une action est bien autorisée pour une ressource donnée (bord d'écriture). */
export const isRbacV2ActionAllowed = (resource: string, action: string): boolean =>
  (rbacV2Abilities as Record<string, readonly string[]>)[resource]?.includes(action) ?? false

/** Renvoie l'action spéciale portée par une ressource, s'il y en a une. */
export const getRbacV2SpecialAction = (resource: RbacV2Resource): RbacV2SpecialAction | undefined =>
  (rbacV2Abilities[resource] as readonly RbacV2Action[]).find(
    (action): action is RbacV2SpecialAction =>
      (RBAC_V2_SPECIAL_ACTIONS as readonly string[]).includes(action),
  )

// --- Libellés (français) — maps plats, ordre de déclaration = ordre d'affichage --------------

/** Clé = segment `module` de la clé de ressource. */
export const RBAC_V2_MODULE_LABELS: Record<string, string> = {
  dashboard: '1. Tableau de bord',
  today: "2. Aujourd'hui",
  carts: '3. Paniers',
  categories: '4. Catégories',
  products: '5. Produits',
  'platform-stock': '6. Stock Plateforme',
  users: '7. Utilisateurs',
  promo: '8. Campagne promo',
  'works-council': '9. Partenariats CE',
  notifications: '10. Notifications',
  stores: '11. Boutiques',
  accounting: '12. Comptabilité',
  quality: '13. Qualité',
  'cart-control': '14. Contrôle panier',
  'daily-sales': '15. Ventes journalières',
  sellers: '16. Vendeurs',
  cameras: '17. Caméras',
  sensors: '18. Capteurs',
  hr: '19. RH & Planning',
  sms: '20. Suivi des SMS',
  visio: '21. Contrôle Interventions (Visio)',
  'real-estate': '22. Foncier & travaux',
  network: '23. Réseau & Boutiques',
  'mailing-lists': '24. Listes de diffusion',
  'roles-admin': '25. Administration › Rôles',
  'feature-flags': '26. Feature Flags',
  previsions: '27. Prévisions',
  'pre-authorizations': '28. Pré-autorisations',
  'interim-agencies': "29. Agences d'intérim",
  'project-tracking': '30. Suivi projet client',
}

/** Clé = `module.sous-onglet`. */
export const RBAC_V2_SUBTAB_LABELS: Record<string, string> = {
  'dashboard.dashboard': 'Tableau de bord',
  'today.today': "Aujourd'hui",
  'carts.carts': 'Paniers',
  'carts.vision-checks': 'Paniers › Vérification visuelle',
  'categories.categories': 'Catégories',
  'categories.internal-categories': 'Catégories internes',
  'categories.reappro': 'Catégories réappro',
  'categories.brands': 'Marques',
  'products.products': 'Produits',
  'platform-stock.platform-stock': 'Stock Plateforme',
  'users.app-users': "Utilisateurs de l'app",
  'users.payment-defaults': 'Défauts de paiement',
  'users.bo-users': 'Utilisateurs du BO',
  'promo.temporary-campaigns': 'Campagnes temporaires',
  'works-council.works-council': 'Partenariats CE',
  'notifications.notifications': 'Notifications',
  'stores.store-list': 'Liste boutiques',
  'stores.store-stocks': 'Boutique › Stocks',
  'stores.current-inventory': 'Boutique › Inventaire en cours',
  'stores.all-inventories': 'Boutique › Tous les inventaires',
  'stores.schedules': 'Boutique › Gestion des horaires',
  'stores.kiosks': 'Boutique › Gestion des kiosks',
  'stores.statistics': 'Boutique › Statistiques',
  'accounting.accounting': 'Comptabilité',
  'quality.search-by-lot': 'Recherche par lot',
  'quality.search-by-article': 'Recherche par article',
  'cart-control.cart-control': 'Contrôle panier',
  'daily-sales.daily-sales': 'Ventes journalières',
  'sellers.sellers': 'Vendeurs',
  'sellers.establishments': 'Établissements',
  'cameras.cameras': 'Caméras',
  'sensors.sensors': 'Capteurs',
  'sensors.fridges': 'Réfrigérateurs',
  'hr.planning': 'Planning',
  'hr.staff': 'Personnel',
  'hr.clocking': 'Pointage',
  'hr.absences': 'Calendrier Absences',
  'sms.sms': 'Suivi des SMS',
  'visio.yesterday-interactions': 'Interactions de la Veille',
  'visio.exchanges-to-evaluate': 'Échanges à Évaluer',
  'visio.overview': "Vue d'ensemble",
  'visio.administration': 'Administration (Visio)',
  'real-estate.map': 'Carte',
  'real-estate.kanban': 'Kanban',
  'real-estate.tasks': 'Tâches',
  'real-estate.calendar': 'Calendrier',
  'real-estate.contacts': 'Contacts',
  'real-estate.areas-of-interest': "Zones d'intérêt",
  'network.network': 'Réseau & Boutiques',
  'mailing-lists.mailing-lists': 'Listes de diffusion',
  'roles-admin.roles': 'Rôles',
  'feature-flags.feature-flags': 'Feature Flags',
  'previsions.previsions': 'Prévisions',
  'pre-authorizations.pre-authorizations': 'Pré-autorisations',
  'interim-agencies.interim-agencies': "Agences d'intérim",
  'project-tracking.board': 'Suivi projet',
}

/** Clé = clé de ressource complète. */
export const RBAC_V2_FEATURE_LABELS: Record<RbacV2Resource, string> = {
  'dashboard.dashboard.global-indicators': 'Indicateurs globaux',
  'today.today.daily-tracking': 'Suivi du jour (classements ventes qté/CA)',
  'carts.carts.cart': 'Paniers (liste & détail)',
  'carts.vision-checks.check': 'Vérification visuelle des paniers',
  'categories.categories.standard': 'Catégorie standard',
  'categories.internal-categories.internal': 'Catégorie interne',
  'categories.reappro.category': 'Catégorie de réappro',
  'categories.reappro.config': 'Configuration de réappro (stocks min)',
  'categories.reappro.simulation': 'Simulation générale de réappro',
  'categories.brands.brand': 'Marque',
  'products.products.product': 'Produit (fiche, identité, catégories)',
  'platform-stock.platform-stock.stock': 'Stock plateforme (quantités & lots)',
  'users.app-users.user': "Utilisateur de l'app",
  'users.app-users.ban': 'Bannissement utilisateur',
  'users.app-users.solvency': 'Solvabilité & moyens de paiement (Conecs)',
  'users.payment-defaults.default': 'Défaut de paiement',
  'users.bo-users.user': 'Utilisateur du BO',
  'users.bo-users.api-key': "Clés API d'un compte du BO",
  'promo.temporary-campaigns.campaign': 'Campagne temporaire',
  'promo.temporary-campaigns.single-use-codes': 'Codes promo à usage unique',
  'works-council.works-council.partnership': 'Partenariat CE',
  'notifications.notifications.notification': 'Notification',
  'notifications.notifications.duplication': 'Duplication de notification',
  'stores.store-list.store': 'Boutique',
  'stores.store-stocks.stock': 'Stock / valorisation / anti-gaspi boutique',
  'stores.current-inventory.inventory': 'Inventaire boutique',
  'stores.current-inventory.validation': "Validation d'inventaire",
  'stores.all-inventories.history': 'Historique des inventaires',
  'stores.schedules.schedule': 'Horaires boutique',
  'stores.kiosks.kiosk': 'Kiosk / borne',
  'stores.statistics.statistics': 'Statistiques boutique',
  'accounting.accounting.figures': 'Chiffres comptables (ventes, TVA, impayés)',
  'accounting.accounting.balance': 'Bilan comptable',
  'accounting.accounting.payments': 'Paiements',
  'accounting.accounting.works-council-history': 'Historiques de CE',
  'accounting.accounting.warnings': 'Avertissements comptables',
  'quality.search-by-lot.lot-traceability': 'Traçabilité par lot',
  'quality.search-by-article.article-traceability': 'Traçabilité par article',
  'cart-control.cart-control.support-mode': 'Mode support (prise de main caisse)',
  'daily-sales.daily-sales.daily-sales': 'Ventes journalières',
  'daily-sales.daily-sales.auto-calculation': 'Calcul automatique des ventes',
  'sellers.sellers.seller': 'Vendeur',
  'sellers.establishments.establishment': 'Établissement',
  'cameras.cameras.stream': 'Flux caméras (par boutique)',
  'cameras.cameras.config': 'Configuration des caméras',
  'sensors.sensors.sensor': 'Capteur',
  'sensors.fridges.fridge': 'Réfrigérateur',
  'hr.planning.weekly-planning': 'Planning hebdomadaire (créneaux)',
  'hr.staff.collaborator': 'Fiche collaborateur',
  'hr.clocking.clocking': 'Pointage',
  'hr.absences.absence': 'Absence',
  'sms.sms.sent': 'SMS envoyés',
  'visio.yesterday-interactions.interaction': 'Interactions de la veille',
  'visio.exchanges-to-evaluate.exchange': 'Échange à évaluer',
  'visio.exchanges-to-evaluate.evaluation': "Évaluation d'un échange",
  'visio.overview.overview': "Vue d'ensemble (boutique / opérateur)",
  'visio.administration.administration': 'Administration du module Visio',
  'real-estate.map.map': 'Carte foncière & indicateurs',
  'real-estate.kanban.project': 'Projet foncier (pipeline)',
  'real-estate.kanban.archive': "Archivage/reprise d'un projet",
  'real-estate.tasks.task': 'Tâche',
  'real-estate.calendar.calendar': 'Calendrier foncier',
  'real-estate.contacts.contact': 'Contact',
  'real-estate.areas-of-interest.area': "Zone d'intérêt",
  'network.network.network-dashboard': 'Tableau de bord réseau (comparaison)',
  'mailing-lists.mailing-lists.mailing-list': 'Liste de diffusion & destinataires',
  'roles-admin.roles.role': 'Rôle & permissions',
  'roles-admin.roles.admin-assignment': "Attribution d'admins à un rôle",
  'feature-flags.feature-flags.feature-flag': 'Feature flag (activation/désactivation)',
  'previsions.previsions.forecast': 'Prévision de CA (projection & backtest)',
  'pre-authorizations.pre-authorizations.session':
    'Session de pré-autorisation (liste, relecture vidéo, conservation du montant)',
  'interim-agencies.interim-agencies.agency': "Agence d'intérim",
  'project-tracking.board.ticket': 'Demandes (kanban, synthèses, fiche)',
  'project-tracking.board.recette': 'Recette client (valider / signaler un souci)',
  'project-tracking.board.comment': 'Commentaire sur une demande',
}

/** Libellé de chaque action (partagée ou spéciale). */
export const RBAC_V2_ACTION_LABELS: Record<RbacV2Action, string> = {
  read: 'Voir',
  add: 'Ajouter',
  edit: 'Modifier',
  delete: 'Supprimer',
  export: 'Exporter',
  import: 'Importer',
  'launch-reappro-simulation': 'Lancer la simulation',
  'send-ban-email': 'Relancer par email',
  'assign-bo-role': 'Assigner un rôle',
  'validate-inventory': "Valider l'inventaire",
  'sync-schedule': 'Enregistrer & synchroniser',
  'restart-kiosk': 'Redémarrer le kiosk',
  'enter-support-mode': 'Passer en mode support',
  'launch-sales-calculation': 'Lancer le calcul',
  'validate-clocking': 'Valider le pointage',
  'resend-unread-sms': 'Relancer les non-lus',
  'archive-real-estate-project': 'Archiver / reprendre',
  'start-intervention': 'Démarrer une intervention',
}

// --- DTOs (permissions de rôle v2) ------------------------------------------------------------

export interface RbacV2RolePermission {
  resource: RbacV2Resource
  action: RbacV2Action
  /** Absent = `global`. N'est renseigné que sur les couples de `RBAC_V2_SCOPED_PERMISSIONS`. */
  scope?: RbacV2Scope
}

export interface ReplaceRbacV2RolePermissionsRequest {
  permissions: RbacV2RolePermission[]
}
